import { Component, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { captureException } from "@/utils/sentry";
// @ts-ignore - i18n.js doesn't have type definitions
import i18n from "@/i18n";

interface PageErrorBoundaryProps {
  children: ReactNode;
  fallbackMessage?: string;
  onReset?: () => void;
}

interface PageErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class PageErrorBoundary extends Component<
  PageErrorBoundaryProps,
  PageErrorBoundaryState
> {
  constructor(props: PageErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Page error boundary caught error:', error, errorInfo);
    
    captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        component: 'PageErrorBoundary',
      },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
          <div className="rounded-full bg-destructive/10 p-4">
            <AlertCircle className="h-12 w-12 text-destructive" />
          </div>
          
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">
              {i18n.t('errorBoundary.title')}
            </h2>
            <p className="text-muted-foreground text-sm">
              {this.props.fallbackMessage || i18n.t('errorBoundary.description')}
            </p>
          </div>

          <div className="flex gap-2">
            <Button onClick={this.handleReset} variant="outline">
              {i18n.t('errorBoundary.tryAgainButton')}
            </Button>
            <Button onClick={() => window.location.reload()}>
              {i18n.t('errorBoundary.refreshPageButton')}
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
